"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.concentrationLimitPointerMethods = void 0;
/* eslint-disable no-console */
const common_1 = require("@finley-technologies/common");
const extractGroupingField = (receivable) => {
    // we used to group by receivable.baseFields.obligor, but now grouping by parent_company
    const parentCompany = receivable.baseFields.parent_company;
    (0, common_1.finleyAssert)(typeof parentCompany === 'string', `parent_company must be a string [found:${parentCompany}] for [externalReceivableId:${receivable.externalReceivableId}]`);
    return parentCompany;
};
exports.concentrationLimitPointerMethods = [
    {
        method: 'leucadiaNatixis1.obligorRatingExcess',
        pointerImplementation: ({ concentrationReceivables, totalConcentrationExposure, }) => {
            const kickouts = {};
            // receivables belongs to obligors.
            // 1st find the total exposure by each obligor. and the maximum exposure
            // allowed by each obligor (depending on the group they belong to)
            // each receivable is part of some obligor
            const obligorMap = concentrationReceivables.reduce((acc, receivable) => {
                const obligor = extractGroupingField(receivable);
                const exposure = receivable.concentrationLimitFields
                    .eligibleExposureLessConcentrationLimitKickouts;
                // a receivable (or obligor) is categorized as Group A, B, C, or D. These groups are each assigned a rate.
                // The rate is pre=computed and stored in the dynamicIntermediateFields of the receivable.
                const obligorGroupLimit = receivable.dynamicIntermediateFields.obligorGroupLimit;
                (0, common_1.finleyAssert)(typeof obligorGroupLimit === 'number', `obligorGroupLimit must be a number. [Found:${obligorGroupLimit}] for [finleyReceivableId:${receivable.finleyReceivableId}]`);
                const obligorData = acc.get(obligor);
                if (!obligorData) {
                    acc.set(obligor, {
                        totalExposure: exposure,
                        obligorGroupLimit,
                    });
                    return acc;
                }
                (0, common_1.finleyAssert)(obligorData.obligorGroupLimit === obligorGroupLimit, `[multiple obligorGroupLimits:(${obligorData.obligorGroupLimit}, ${obligorGroupLimit})] found for same [obligor:${obligor}]`);
                // if the obligor is already in the map, add the exposure to the existing one
                obligorData.totalExposure += exposure;
                return acc;
            }, new Map());
            // compute excess exposure for each obligor
            const excessExposureByObligor = new Map();
            obligorMap.forEach((obligorData, obligor) => {
                const obligorRatingLimit = totalConcentrationExposure * obligorData.obligorGroupLimit;
                const excessExposure = Math.max(obligorData.totalExposure - obligorRatingLimit, 0);
                excessExposureByObligor.set(obligor, excessExposure);
            });
            // now for each receivable, check the obligor it belongs to and
            // if the exposure is greater than the limit, kick out the excess
            concentrationReceivables.forEach((receivable) => {
                const obligor = extractGroupingField(receivable);
                // find excess exposure for the obligor
                const excessExposure = excessExposureByObligor.get(obligor);
                if (excessExposure) {
                    const kickOut = Math.max(Math.min(receivable.concentrationLimitFields
                        .eligibleExposureLessConcentrationLimitKickouts, excessExposure), 0);
                    kickouts[receivable.finleyReceivableId] = kickOut;
                    excessExposureByObligor.set(obligor, excessExposure - kickOut);
                }
            });
            return {
                kickouts,
                calculations: {
                    concentrationAmount: 0, // this applies only for standard concentration limits
                    concentrationLimit: 0, // this applies only for standard concentration limits
                    concentration: 0, // this applies only for standard concentration limits
                },
            };
        },
    },
    // all the previous concentration methods calculate the concentration, but mark them as waived.
    // this method is the final one that actually kicks out the excess exposure, by looking at all the
    // waived concentrations, and finding the excess exposure for each obligor per concentration limit
    // we will kick out maximum excess exposure for each obligor.
    // we will group receivables by obligor and calculate sum the kickouts for each test.
    {
        method: 'leucadiaNatixis1.maximumExcessExposure',
        pointerImplementation: ({ concentrationReceivables }) => {
            const kickouts = {};
            const obligorMap = concentrationReceivables.reduce((acc, receivable) => {
                const obligor = extractGroupingField(receivable);
                const exposure = receivable.concentrationLimitFields
                    .eligibleExposureLessConcentrationLimitKickouts;
                const obligorData = acc.get(obligor) ?? {
                    totalExposure: 0,
                    kickoutByTest: new Map(),
                };
                // look at the waived concentrations and kickouts for this receivable
                // and add them to the obligor's kickoutByTest map
                receivable.concentrationLimitWaivers.forEach((waiver) => {
                    const testName = waiver.propertyKey;
                    const waivedAmount = waiver.waivedAmount ?? 0;
                    if (waivedAmount > 0) {
                        const currentKickout = obligorData.kickoutByTest.get(testName) ?? 0;
                        obligorData.kickoutByTest.set(testName, currentKickout + waivedAmount);
                    }
                });
                obligorData.totalExposure += exposure;
                acc.set(obligor, obligorData);
                return acc;
            }, new Map());
            // we compute excess exposure for each obligor
            // by looking at the maximum kickout for each test
            const excessExposureByObligor = new Map();
            obligorMap.forEach((obligorData, obligor) => {
                // find max exposure for the obligor
                // among all the tests
                const maxKickout = Math.max(...Array.from(obligorData.kickoutByTest.values()), 0);
                excessExposureByObligor.set(obligor, maxKickout);
            });
            // we want to print a nice table of obligor x kickouts by test
            // to help debug the results
            const uniqueTestNames = new Set();
            Array.from(obligorMap.values())
                .flatMap((data) => Array.from(data.kickoutByTest.keys()))
                .forEach((testName) => uniqueTestNames.add(testName));
            // build table rows: one row per obligor, one column per test
            const tableData = Array.from(obligorMap.entries()).map(([obligor, data]) => {
                const row = {
                    obligor,
                    totalExposure: data.totalExposure,
                    excessExposure: excessExposureByObligor.get(obligor) ?? 0,
                };
                uniqueTestNames.forEach((testName) => {
                    row[testName] = data.kickoutByTest.get(testName) ?? null;
                });
                return row;
            });
            console.table(tableData, [
                'obligor',
                'totalExposure',
                'excessExposure',
                ...uniqueTestNames,
            ]);
            // now for each receivable, check the obligor it belongs to and
            // if the exposure is greater than the limit, kick out the excess
            concentrationReceivables.forEach((receivable) => {
                const obligor = extractGroupingField(receivable);
                // find excess exposure for the obligor
                const excessExposure = excessExposureByObligor.get(obligor);
                if (excessExposure) {
                    const kickOut = Math.max(Math.min(receivable.concentrationLimitFields
                        .eligibleExposureLessConcentrationLimitKickouts, excessExposure), 0);
                    kickouts[receivable.finleyReceivableId] = kickOut;
                    excessExposureByObligor.set(obligor, excessExposure - kickOut);
                }
            });
            return {
                kickouts,
                calculations: {
                    concentrationAmount: 0, // this applies only for standard concentration limits
                    concentrationLimit: 0, // this applies only for standard concentration limits
                    concentration: 0, // this applies only for standard concentration limits
                },
            };
        },
    },
];
//# sourceMappingURL=concentrationLimitPointerMethods.js.map